import { createRouter, createWebHistory } from "vue-router";
import NotFound404 from "@/views/NotFound404.vue";
import NotAuthorized from "@/views/NotAuthorized.vue";
import HomeView from "@/views/HomeView.vue";
import AboutView from "@/views/AboutView.vue";
import LoginView from "@/views/LoginView.vue";
//Lazy import CodesView from "@/views/codes/CodesView.vue";
import CodeDetailsView from "@/views/codes/CodeDetailsView.vue";
import CodeAnalyticsView from "@/views/codes/CodeAnalyticsView.vue";
import AddCodeView from "@/views/codes/AddCodeView.vue";
import EditCodeView from "@/views/codes/EditCodeView.vue";
import CodeImportExportView from "@/views/codes/CodeImportExportView.vue";
import Reload from "@/views/Reload.vue";
import Auth from "@/views/Auth.vue";
import Logout from "@/views/Logout.vue";
import { useRoute } from "vue-router";
import { isAuthenticated } from "@/libs/auth";

const SITE_NAME = "QR Code Experts";
const SITE_ROOT = (process.env.VUE_APP_ROOT_URL || "https://qrcodeexperts.net/").replace(/\/+$/, "");
const DEFAULT_DESCRIPTION =
    "Managed QR code publishing with redirect support, analytics, and optional GTIN/UPC barcode assets.";

function ensureHeadTag(selector, tagName, attributes) {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement(tagName);
        document.head.appendChild(el);
    }
    Object.entries(attributes).forEach(([key, value]) => {
        el.setAttribute(key, value);
    });
    return el;
}

function updateRouteMetadata(to) {
    const pageTitle = to.meta.title || SITE_NAME;
    const description = to.meta.description || DEFAULT_DESCRIPTION;
    const robots = to.meta.robots || "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1";
    const canonicalPath = typeof to.meta.canonicalPath === "string" ? to.meta.canonicalPath : to.path;
    const canonicalUrl = new URL(canonicalPath, `${SITE_ROOT}/`).toString();

    document.title = pageTitle;
    ensureHeadTag('meta[name="description"]', "meta", {
        name: "description",
        content: description,
    });
    ensureHeadTag('meta[name="robots"]', "meta", {
        name: "robots",
        content: robots,
    });
    ensureHeadTag('link[rel="canonical"]', "link", {
        rel: "canonical",
        href: canonicalUrl,
    });
    ensureHeadTag('meta[property="og:title"]', "meta", {
        property: "og:title",
        content: pageTitle,
    });
    ensureHeadTag('meta[property="og:description"]', "meta", {
        property: "og:description",
        content: description,
    });
    ensureHeadTag('meta[property="og:url"]', "meta", {
        property: "og:url",
        content: canonicalUrl,
    });
}

const routes = [
    {
        path: "/",
        name: "home",
        component: HomeView,
        meta: {
            display: "Home",
            title: "QR Code Experts | Managed QR Codes and Barcode Assets",
            description:
                "Create and manage QR codes with redirect support, analytics, and optional GTIN/UPC barcode assets from one workspace.",
            showNavigation: true,
        },
        beforeEnter: (to, from, next) => {
            if (to.query.of === 1) {
                next({ name: "codead" });
            } else {
                next(); // Default to App1
            }
        },
    },
    {
        path: "/codead/:codeid?",
        name: "codead",
        beforeEnter: (to) => {
            const { codeid } = to.params;
            const destination = codeid
                ? `https://qrexl.ink/${encodeURIComponent(codeid)}`
                : "https://qrexl.ink";

            window.location.replace(destination);
            return false;
        },
        meta: {
            display: "Code Ad",
            title: "Redirecting | QR Code Experts",
            description: "Redirect wrapper route.",
            robots: "noindex,nofollow",
            hideNavbar: true,
            showNavigation: false,
        },
    },
    {
        path: "/codes",
        name: "codes",
        component: () => import("../views/codes/CodesView.vue"),
        meta: {
            display: "Codes",
            title: "Code Inventory | QR Code Experts",
            description: "Private code inventory and management workspace.",
            robots: "noindex,nofollow",
            showNavigation: true,
        },
    },
    {
        path: "/code/:codeid?",
        name: "CodeDetails",
        component: CodeDetailsView,
        meta: {
            display: "Code Details",
            title: "Code Details | QR Code Experts",
            description: "Private code details, asset previews, and management tools.",
            robots: "noindex,nofollow",
            hideNavbar: true,
            showNavigation: true,
        },
        props: true,
    },
    {
        path: "/code/:codeid/analytics",
        name: "code analytics",
        component: CodeAnalyticsView,
        meta: {
            display: "Analytics",
            title: "Code Analytics | QR Code Experts",
            description: "Private redirect analytics and reporting for managed QR codes.",
            robots: "noindex,nofollow",
            hideNavbar: true,
            showNavigation: true,
        },
        props: true,
    },
    {
        path: "/addcode",
        name: "add code",
        component: AddCodeView,
        meta: {
            display: "Add Code",
            title: "Add Code | QR Code Experts",
            description: "Private code creation workspace.",
            robots: "noindex,nofollow",
            authRequired: true,
            showNavigation: true,
        },
        beforeEnter: (to, from, next) => {
            if (isAuthenticated()) {
                // Replace 'yourCondition' with your logic
                next(); // Allow navigation
            } else {
                next("/");
            }
        },
    },
    {
        path: "/codes/import-export",
        name: "code import export",
        component: CodeImportExportView,
        meta: {
            display: "Import / Export",
            title: "Import and Export Codes | QR Code Experts",
            description: "Download the XLSX template, validate spreadsheet changes, and commit code imports.",
            robots: "noindex,nofollow",
            authRequired: true,
            hideNavbar: true,
            showNavigation: true,
        },
        beforeEnter: (to, from, next) => {
            if (isAuthenticated()) {
                next();
            } else {
                next("/");
            }
        },
    },
    {
        path: "/editcode/:codeid?",
        name: "edit code",
        component: EditCodeView,
        meta: {
            display: "Edit Code",
            title: "Edit Code | QR Code Experts",
            description: "Private code editing workspace.",
            robots: "noindex,nofollow",
            hideNavbar: true,
            authRequired: true,
            showNavigation: true,
        },
        props: true,
        beforeEnter: (to, from, next) => {
            if (isAuthenticated()) {
                // Replace 'yourCondition' with your logic
                next(); // Allow navigation
            } else {
                next("/");
            }
        },
    },
    {
        path: "/about",
        name: "about",
        component: AboutView,
        //use meta to pre-set Nav button value when lazy loading
        meta: {
            display: "About",
            title: "About QR Code Experts",
            description:
                "Learn what QR Code Experts makes available, including managed QR code operations, redirect support, analytics, and optional barcode assets.",
            showNavigation: true,
        },
    },
    {
        path: "/login",
        name: "login",
        component: LoginView,
        meta: {
            display: "Login",
            title: "Login | QR Code Experts",
            description: "Request a magic link by email to sign in to QR Code Experts.",
            robots: "noindex,nofollow",
            guestOnly: true,
            showNavigation: true,
        },
    },
    {
        path: "/logout",
        name: "logout",
        component: Logout,
        //use meta to pre-set Nav button value when lazy loading
        meta: {
            display: "Sign Out",
            title: "Sign Out",
            description: "Private sign-out route.",
            robots: "noindex,nofollow",
            authRequired: true,
            showNavigation: true,
        },
    },
    {
        path: "/reload",
        name: "reload",
        component: Reload,
        //use meta to pre-set Nav button value when lazy loading
        meta: {
            display: "Reload",
            title: "Reload",
            description: "Private reload route.",
            robots: "noindex,nofollow",
            hideNavbar: true,
            showNavigation: true,
        },
    },
    {
        path: "/auth",
        name: "auth",
        component: Auth,
        //use meta to pre-set Nav button value when lazy loading
        meta: {
            title: "Sign In | QR Code Experts",
            description: "Private authentication route.",
            robots: "noindex,nofollow",
            hideNavbar: true,
            showNavigation: false,
        },
    },
    {
        path: "/notauthorized",
        name: "notauthorized",
        component: NotAuthorized,
        //use meta to pre-set Nav button value when lazy loading
        meta: {
            title: "Not Authorized | QR Code Experts",
            description: "Authorization status page.",
            robots: "noindex,nofollow",
            hideNavbar: true,
            showNavigation: true,
        },
    },
    //catch all 404
    {
        path: "/:catchAll(.*)",
        name: "notfound",
        component: NotFound404,
        meta: {
            title: "Page Not Found | QR Code Experts",
            description: "The requested page could not be found.",
            robots: "noindex,nofollow",
            hideNavbar: true,
            showNavigation: true,
        },
    },
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
});

const CHUNK_RELOAD_GUARD = "qrce-chunk-reload";

function isChunkLoadFailure(error) {
    const message = `${error?.message || error || ""}`;
    return (
        message.includes("Loading chunk") ||
        message.includes("ChunkLoadError") ||
        message.includes("Failed to fetch dynamically imported module")
    );
}

router.beforeEach((to, from, next) => {
    updateRouteMetadata(to);
    next();
});

router.afterEach(() => {
    if (typeof window === "undefined") return;
    try {
        window.sessionStorage.removeItem(CHUNK_RELOAD_GUARD);
    } catch {
        // ignore storage issues
    }
});

router.onError((error, to) => {
    if (typeof window === "undefined" || !isChunkLoadFailure(error)) return;

    let alreadyReloaded = false;
    try {
        alreadyReloaded = window.sessionStorage.getItem(CHUNK_RELOAD_GUARD) === "1";
    } catch {
        // ignore storage issues
    }

    if (alreadyReloaded) {
        console.error("Chunk reload failed after refresh.", error);
        return;
    }

    try {
        window.sessionStorage.setItem(CHUNK_RELOAD_GUARD, "1");
    } catch {
        // ignore storage issues
    }

    const target = to?.fullPath || window.location.pathname || "/";
    window.location.assign(target);
});

/*
router.beforeEach((to, from, next) => {
    if (
        to.name !== "login"
        //    && !isAuthenticated
    )
        next({ name: "login" })
    else next();
});
*/

export default router;
